import { useApplyPendingEventContext } from "@features/access";
import { useAuthStore } from "@features/authentication/stores/authStore";
import { isCompleteUser } from "@schemas/users.validators";
import { PageLoading } from "@shared/components/PageLoading";
import useCurrentUser from "@shared/queries/useCurrentUser";
import { Navigate, Outlet, useLocation } from "react-router-dom";

/**
 * Protected route component that checks authentication status and profile completion.
 * Redirects unauthenticated users to `/auth` (phone sign-in).
 * Redirects users with incomplete profiles to onboarding.
 */
export function ProtectedRoute() {
  const { status, userId } = useAuthStore();
  const { user, isLoading } = useCurrentUser();
  const location = useLocation();

  useApplyPendingEventContext();

  if (status === "pending") {
    return <PageLoading message="Checking authentication..." fullScreen />;
  }

  // Show error state if authentication failed
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Authentication failed. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  if (status === "unauthenticated" || !userId) {
    // Redirect to auth if not authenticated
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return <PageLoading message="Loading user profile..." fullScreen />;
  }

  const needsOnboarding = !user || !isCompleteUser(user);

  if (needsOnboarding) {
    if (location.pathname !== "/onboarding") {
      return <Navigate to="/onboarding" replace />;
    }
  }

  if (user && isCompleteUser(user) && location.pathname === "/onboarding") {
    return <Navigate to="/discover" replace />;
  }

  return <Outlet />;
}
